import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { useAutomationsSelector } from '__pages/AutomationEngine/store/react';
import {
  getMergeArgs,
  getAutomationIntializationVariables,
} from 'pages/AutomationEngine/store/selectors';

import { useMergeFields } from 'hooks/useMergeFields';
import { BasicRichTextEditor } from '__components/WYSIWYG';
import { createJsonContent } from '__components/WYSIWYG/utils/content';

import { OutlineLabel } from '__components/Inputs/InputControl';
import { StyledLoader } from '../../styles';

export type StringInputPayload = {
  valueHtml: string;
  value: string;
};

const Wrapper = styled.div`
  span.insert-btn {
    margin: 0;
  }
`;

type Props = {
  title: string;
  value: string;
  valueHtml?: string;
  dataQa: string;
  disabled: boolean;
  enableAutomationVariables: boolean;
  onChange: (payload: StringInputPayload, isValid: boolean, meta?: any) => void;
  error?: string[];
  setError: (prop: 'value', error: string[]) => void;
  validate?: any;
};

const emptyArray: any = [];

export const StringInput: FC<Props> = ({
  title: titleProp,
  value,
  valueHtml,
  dataQa,
  disabled,
  enableAutomationVariables,
  onChange,
}) => {
  const { t } = useTranslation();

  const title = titleProp || t('Value');
  const mergeArgs = useAutomationsSelector(getMergeArgs);
  const automationVariables =
    useAutomationsSelector(getAutomationIntializationVariables) || emptyArray;

  const { mergeFields, loading } = useMergeFields({
    ...mergeArgs,
    addAutomationHistory: true,
    addAdditionalOptions: true,
    automationVariables: enableAutomationVariables
      ? automationVariables
      : undefined,
  });

  const textareaChangeEvent = useCallback(
    ({ editor }: any) => {
      const value = editor.getText({ blockSeparator: '\n' });
      const valueHtml = editor.getHTML();

      onChange({ value, valueHtml }, true);
    },
    [onChange]
  );

  const initialValue = valueHtml ?? createJsonContent(value);

  return (
    <StyledLoader loading={loading}>
      <Wrapper data-qa={dataQa}>
        <OutlineLabel>{title}</OutlineLabel>
        <BasicRichTextEditor
          initialValue={initialValue}
          onChange={textareaChangeEvent}
          placeholder={t('Enter Value')}
          enableMergeFields
          mergeFields={mergeFields}
          enableLists={false}
          enableTextStyles={false}
          enableResize={false}
          initialHeight={136}
          borderHoverColor="var(--color-button-secondary-hover)"
          disabled={disabled}
          appendMergeFieldExtraSpace={false}
          wideMergePicker
        />
      </Wrapper>
    </StyledLoader>
  );
};
